import { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthenticatedClient } from '../Utils/tokenHelper';
import { useAccount, useMsal } from '@azure/msal-react';
import { enableFrontdoor } from '../Utils/features';
import { BATCH_UPLOAD } from '../Constants/fileCategory.js';
import { TEXT_FILE, ZIP_FILE } from '../Constants/fileTypes.js';
import { STATUS_FAILED, STATUS_UPLOADED, STATUS_UPLOADING } from '../Constants/uploadStatus.js';
import { MB_30 } from '../Constants/chunkDefaults.js';

const BASE_URL = enableFrontdoor ? process.env.REACT_APP_DATAACCESS_API_BASE_URL_FRONTDOOR : process.env.REACT_APP_DATAACCESS_API_BASE_URL;

const CHUNK_SIZE_CUT_OFF = MB_30 * 2;
const FILE_DELIMETER = '.';
const BATCH_DELIMTER = '_';
const CHUNK_TOKEN = "part";
const BATCH_FILE_TOKEN = "batch";
const DEFAULT_CHUNK = 0;

export default function useBatchDataAccessApi() {
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState();
    const { accounts, instance } = useMsal();
    const account = useAccount(accounts[0]);

    const uploadBatchApi = async (file, batchId, setProgress, setUploading, setUploadError, is837FormatEnabled) => {
        setUploading(true);
        setUploadError('');
        setProgress(0);

        const uploadUrl = `${BASE_URL}/dataaccess/${BATCH_UPLOAD}/`;
        let successfulUpload = true;

        let fileTypeForApi = "";
        let fileType = file.type;

        if (fileType === 'text/plain') {
            fileTypeForApi = TEXT_FILE;
        } else if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
            fileTypeForApi = ZIP_FILE;
        }

        try {
            // Dont want to chunk up the 837s right now
            if (file.size > CHUNK_SIZE_CUT_OFF) {
                const chunks = Math.ceil(file.size / MB_30);

                const fileRanges = [];
                // Create file ranges for each chunk
                for (let i = 0; i < chunks; i++) {
                    const from = i * MB_30;
                    const to = (i === chunks - 1) ? file.size : (i + 1) * MB_30;
                    fileRanges.push({ from, to, part: i, progress: 0, status: '' });
                }

                const intervalReference = setInterval(() => {
                    const totalProgress = fileRanges.map(sc1 => sc1.progress).reduce((pv, cv) => pv + cv, 0) / fileRanges.length;
                    setProgress(totalProgress);
                }, 2000);

                try {
                    // Loop until all chunks are uploaded
                    while (fileRanges.some(c => c.status !== STATUS_UPLOADED && c.status !== STATUS_FAILED)) {
                        const unuploadedChunks = fileRanges.filter(fr => fr.status !== STATUS_UPLOADED);
                        const selectedChunks = unuploadedChunks.slice(0, 10);
            
                        const uploadingChunks = selectedChunks.map(sc => new Promise((resolve) => {
                            const chunk = file.slice(sc.from, sc.to);
                            sc.status = STATUS_UPLOADING;
                            uploadChunkApi(chunk, uploadUrl, `${BATCH_FILE_TOKEN}${BATCH_DELIMTER}${batchId}${BATCH_DELIMTER}${fileTypeForApi}${FILE_DELIMETER}${CHUNK_TOKEN}${sc.part}`, sc.part, fileRanges)
                                .then(() => {
                                    sc.status = STATUS_UPLOADED;
                                })
                                .catch(e => {
                                    console.error(`Failed to upload chunk ${sc.part}`, e);
                                    sc.error = e;
                                    sc.status = STATUS_FAILED;
                                })
                                .finally(() => {
                                    resolve();
                                });
                        }));
            
                        await Promise.all(uploadingChunks);
            
                        const failedUploads = fileRanges.filter(fr => fr.status === STATUS_FAILED);
                        if (failedUploads.length > 0) {
                            setUploadError('Error occurred during upload. Failed to upload file');
                            successfulUpload = false;
                            break;
                        }
                    }
                } finally {
                    clearInterval(intervalReference);
                    setUploading(false);
                }
            } else {
                // Upload non chunked file
                try {
                    const fileType = file.name.split(FILE_DELIMETER).pop();
                    await getAuthenticatedClient({
                        url: `${uploadUrl}${BATCH_FILE_TOKEN}${BATCH_DELIMTER}${batchId}${BATCH_DELIMTER}${fileType}${FILE_DELIMETER}${CHUNK_TOKEN}${DEFAULT_CHUNK}`,
                        body: file,
                        method: 'POST',
                        account,
                        msalInstance: instance,
                        headers: {
                            'Content-Type': 'application/octet-stream'
                        },
                        onUploadProgress: (event) => {
                            const totalProgress = (event.loaded / event.total) * 100;
                            setProgress(totalProgress);
                        }
                    });
                } catch (error) {
                    setUploadError(error.message);
                    console.error("An error occurred while uploading to endpoint: ", error);
                    successfulUpload = false;
                } finally {
                    setUploading(false);
                }
            }
        } catch (error) {
            setUploadError(error.message);
            console.error("An error occurred during the upload process: ", error);
            successfulUpload = false;
        } finally {
            setUploading(false);
            return successfulUpload;
        }
    };

    const uploadChunkApi = async (chunk, uploadUrl, chunkName, partNumber, fileRanges) => {
        try {
            await getAuthenticatedClient({
                url: uploadUrl + chunkName,
                body: chunk,
                account,
                msalInstance: instance,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/octet-stream'
                },
                onUploadProgress: (event) => {
                    const chunkProgress = (event.loaded / event.total) * 100;
                    fileRanges[partNumber].progress = chunkProgress;
                }
            });
        } catch (error) {
            throw new Error(`Failed to upload chunk ${partNumber}: ${error.message}`);
        }
    };

    const uploadFile = async (file, zipFileName) => {
        setUploading(true);
        setError(undefined);

        try {
            const { data } = await (await getAuthenticatedClient({
                url: `${BASE_URL}/dataaccess?direction=upload`,
                method: 'GET',
                account, 
                msalInstance: instance
            }));
            
            const sasURL = new URL(data.sasToken);
            const urlParts = [
                sasURL.origin,
                sasURL.pathname,
                `/${zipFileName}`,
                sasURL.search
            ];

            const urlWithFileName = urlParts.join('');
            
            try {
                await axios.put(urlWithFileName, file, {
                    headers: {
                        'Content-Type': 'application/zip',
                        'x-ms-blob-type': 'BlockBlob'
                    },
                });

                return true;
            } catch (e1) {
                setError(e1);
                console.error(`An error occurred while uploading to endpoint ${urlWithFileName}: `, e1);
                return false;
            }
        } catch (e2) {
            setError(e2);
            console.error("An error occurred while retrieving the SAS URI: ", e2);
            return false;
        } finally {
            setUploading(false);
        }
    }

    const downloadFile = async (batchId) => {
        setDownloading(true);
        setError(undefined);

        try {
            const { data } = await (await getAuthenticatedClient({
                method: 'GET',
                url: `${BASE_URL}/dataaccess?direction=download`,
                msalInstance: instance,
                account
            }));

            const sasURL = new URL(data.sasToken);
            const urlParts = [
                sasURL.origin,
                sasURL.pathname,
                `/batch_${batchId}.zip`,
                sasURL.search
            ]
            const urlWithFileName = urlParts.join('');

            try {
                const downloadResponse = await axios.get(urlWithFileName, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'x-ms-blob-type': 'BlockBlob'
                    },
                    responseType: 'blob'
                });

                const fileBlob = downloadResponse.data;

                return fileBlob;
            } catch (error) {
                setError(error);
                if (error.response && error.response.status === 404) {
                    console.error("BatchID does not exist: ", batchId);
                }
                console.error(`An error occurred while downloading from: ${urlWithFileName}`, error);
                return null;
            }
        } catch (error) {
            setError(error);
            console.error("An error occurred while retrieving the SAS URI: ", error);
            return null;
        } finally {
            setDownloading(false);
        }
    }

    return {
        download: downloadFile,
        upload: uploadFile,
        uploadBatchApi,
        uploading,
        downloading,
        error
    };
}
